import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PDFDownload } from '../common/PDFViewer';
import { PDFViewerModal } from '../common/PDFViewerModal';
// import { useAuth } from "../../hooks/useSupabaseAuth"

interface ContentItem {
  id: number;
  title: string;
  description: string;
  price_cents: number;
  currency: string;
  tags?: string;
  sort_order: number;
  created_at: string;
}

interface ClientPurchase {
  id: number;
  content_item_id: number;
  amount_paid: number;
  purchase_status: string;
  purchased_at: string;
  last_accessed_at?: string;
  access_count: number;
}

interface ContentWithPurchaseStatus extends ContentItem {
  is_purchased: boolean;
  purchase_info?: ClientPurchase;
}

export const ClientContentLibrary: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [contentItems, setContentItems] = useState<ContentWithPurchaseStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'available' | 'purchased'>('all');
  const [purchasing, setPurchasing] = useState<number | null>(null);
  const [downloadingContent, setDownloadingContent] = useState<{ id: number; title: string } | null>(null);
  const [viewingContent, setViewingContent] = useState<{ id: number; title: string } | null>(null);

  useEffect(() => {
    fetchContentLibrary();
  }, []);

  // Handle direct content viewing from URL parameter
  useEffect(() => {
    const viewContentId = searchParams.get('viewContent');
    if (viewContentId && contentItems.length > 0) {
      const contentId = parseInt(viewContentId);
      const content = contentItems.find(item => item.id === contentId && item.is_purchased);
      
      if (content) {
        setViewingContent({ id: content.id, title: content.title });
      }
    }
  }, [searchParams, contentItems]);

  const fetchContentLibrary = async () => {
    try {
      const response = await fetch('/api/client/content-library', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch content library');
      }

      const data = await response.json();
      
      if (data.success && data.data && Array.isArray(data.data.content_items)) {
        setContentItems(data.data.content_items);
      } else {
        console.warn('Invalid data structure received:', data);
        setContentItems([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content library');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (contentId: number) => {
    setPurchasing(contentId);
    setError(null);

    try {
      const response = await fetch('/api/client/purchase-content', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contentId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate purchase');
      }

      const data = await response.json();
      
      if (data.success && data.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const handleDownloadContent = (contentId: number, title: string) => {
    // Trigger download modal
    setDownloadingContent({ id: contentId, title });
  };

  const handleViewContent = (contentId: number, title: string) => {
    // Trigger PDF viewer modal
    setViewingContent({ id: contentId, title });
  };

  const handleCloseDownload = () => {
    setDownloadingContent(null);
    // Refresh library to update access count
    fetchContentLibrary();
  };

  const handleCloseViewer = () => {
    setViewingContent(null);
    // Refresh library to update access count
    fetchContentLibrary();
  };

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const filteredContent = contentItems.filter(item => {
    switch (filter) {
      case 'available':
        return !item.is_purchased;
      case 'purchased':
        return item.is_purchased;
      default:
        return true;
    }
  });

  const purchasedCount = contentItems.filter(item => item.is_purchased).length;
  const availableCount = contentItems.filter(item => !item.is_purchased).length;

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading content library...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Premium Content Library</h2>
        
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              filter === 'all' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All ({contentItems.length})
          </button>
          <button
            onClick={() => setFilter('available')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              filter === 'available' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Available ({availableCount})
          </button>
          <button
            onClick={() => setFilter('purchased')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              filter === 'purchased' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Purchased ({purchasedCount})
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      {contentItems.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <div className="text-gray-400 text-lg mb-2">📚</div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">No Content Available</h3>
          <p className="text-gray-500">
            There's no premium content available at the moment. Check back later for new resources!
          </p>
        </div>
      ) : filteredContent.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <div className="text-gray-400 text-lg mb-2">🔍</div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No {filter} content
          </h3>
          <p className="text-gray-500">
            {filter === 'purchased' 
              ? "You haven't purchased any content yet." 
              : "All available content has been purchased."}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredContent.map((item) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">📄</div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      item.is_purchased 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {item.is_purchased ? 'Purchased' : 'Available'}
                    </span>
                  </div>
                  
                  <div className="text-lg font-bold text-gray-900">
                    {formatPrice(item.price_cents)}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {item.description}
                </p>

                {item.tags && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {JSON.parse(item.tags).map((tag: string, index: number) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {item.is_purchased && item.purchase_info && (
                  <div className="bg-green-50 border border-green-200 rounded p-3 mb-4 text-sm">
                    <div className="text-green-700">
                      <div>Purchased: {new Date(item.purchase_info.purchased_at).toLocaleDateString()}</div>
                      <div>Accessed: {item.purchase_info.access_count} times</div>
                      {item.purchase_info.last_accessed_at && (
                        <div>Last viewed: {new Date(item.purchase_info.last_accessed_at).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {item.is_purchased ? (
                    <>
                      <button
                        onClick={() => handleViewContent(item.id, item.title)}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View PDF
                      </button>
                      <button
                        onClick={() => handleDownloadContent(item.id, item.title)}
                        className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        Download
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handlePurchase(item.id)}
                      disabled={purchasing === item.id}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      {purchasing === item.id ? 'Processing...' : `Purchase ${formatPrice(item.price_cents)}`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {purchasedCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Your Content Library</h4>
          <p className="text-blue-700 text-sm">
            You've purchased {purchasedCount} content item{purchasedCount !== 1 ? 's' : ''}. 
            Download them anytime to read offline.
          </p>
        </div>
      )}

      {/* PDF Download Modal */}
      {downloadingContent && (
        <PDFDownload
          contentId={downloadingContent.id}
          title={downloadingContent.title}
          onClose={handleCloseDownload}
        />
      )}

      {/* PDF Viewer Modal */}
      {viewingContent && (
        <PDFViewerModal
          contentId={viewingContent.id}
          title={viewingContent.title}
          onClose={handleCloseViewer}
        />
      )}
    </div>
  );
};